import { BlockStack, Button, InlineStack, TextField, Text, ButtonGroup } from '@shopify/polaris';
import { ICardMediaAsset } from '@/types/media';
import MediaAssetCard from '../media-asset/MediaAssetCard';
import DismissibleBanner from '../DismissibleBanner';
import { InputWrapper } from '@/features/video-generation-stepper/inputs/InputWrapper';
import { SessionBanner } from '@/types/banners';
import {
  VideoMediaAssetDuration,
  VideoMediaAssetMode,
} from '@/features/ai-video-asset-creator/components/AIVideoAssetCreator';

type ImageToVideoProps = {
  isError?: boolean;
  isGenerating?: boolean;
  generatedMediaAsset?: ICardMediaAsset;
  hideUseAction?: boolean;
  videoPrompt?: string;
  setVideoPrompt?: (prompt: string) => void;
  disabled?: boolean;
  setVideoDuration?: (duration: VideoMediaAssetDuration) => void;
  videoDuration?: VideoMediaAssetDuration;
  videoMode?: VideoMediaAssetMode;
  setVideoMode?: (mode: VideoMediaAssetMode) => void;
};

const PROMPT_MAX_LENGTH = 512;

export const ImageToVideo = ({
  videoPrompt,
  setVideoPrompt,
  isError,
  isGenerating,
  generatedMediaAsset,
  hideUseAction,
  disabled,
  setVideoDuration,
  videoDuration,
  videoMode,
  setVideoMode,
}: ImageToVideoProps) => {
  return (
    <InputWrapper title="Add a prompt" description="Describe how you want the image to be animated">
      <BlockStack gap="600">
        <DismissibleBanner tone="info" sessionBanner={SessionBanner.AnimateToolContentModeration} showBanner={true}>
          All images and prompts are subject to content moderation. Illegible content may fail when generating.
        </DismissibleBanner>
        <BlockStack gap="300">
          <TextField
            disabled={disabled}
            autoComplete="off"
            value={videoPrompt}
            onChange={setVideoPrompt}
            label="Video prompt (optional)"
            helpText={`Specify what type of animation you want in the video. ${PROMPT_MAX_LENGTH} characters max.`}
            placeholder="Enter a prompt for the video"
            multiline={true}
            autoFocus={false}
            maxLength={PROMPT_MAX_LENGTH}
          />

          {setVideoDuration && videoDuration && (
            <BlockStack gap="200">
              <Text as="p" variant="bodyMd">
                Video duration
              </Text>
              <ButtonGroup variant="segmented">
                <Button
                  variant="secondary"
                  pressed={videoDuration === VideoMediaAssetDuration.Default}
                  onClick={() => setVideoDuration(VideoMediaAssetDuration.Default)}
                >
                  5s
                </Button>
                <Button
                  variant="secondary"
                  pressed={videoDuration === VideoMediaAssetDuration.Long}
                  onClick={() => setVideoDuration(VideoMediaAssetDuration.Long)}
                >
                  10s
                </Button>
              </ButtonGroup>
            </BlockStack>
          )}
          {setVideoMode && videoMode && (
            <BlockStack gap="200">
              <Text as="p" variant="bodyMd">
                Video mode
              </Text>
              <ButtonGroup variant="segmented">
                <Button
                  variant="secondary"
                  pressed={videoMode === VideoMediaAssetMode.std}
                  onClick={() => setVideoMode(VideoMediaAssetMode.std)}
                >
                  Std
                </Button>
                <Button
                  variant="secondary"
                  pressed={videoMode === VideoMediaAssetMode.pro}
                  onClick={() => setVideoMode(VideoMediaAssetMode.pro)}
                >
                  Pro
                </Button>
              </ButtonGroup>
            </BlockStack>
          )}
        </BlockStack>
        <DismissibleBanner tone="info" showBanner={isGenerating} sessionBanner={SessionBanner.FinalVideoAvailable}>
          Final video will be also available in &quot;My creations&quot; tab
        </DismissibleBanner>

        {!isError && generatedMediaAsset && (
          <InlineStack align="center">
            <MediaAssetCard
              media={generatedMediaAsset}
              hideUseAction={hideUseAction}
              showProgressBannerWarning={true}
            />
          </InlineStack>
        )}
      </BlockStack>
    </InputWrapper>
  );
};
